import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { Loader2, CheckCircle2, XCircle, ListTodo, Copy, Search } from "lucide-react";
import { format } from "date-fns";

type FeedbackStatus = "pending" | "under_review" | "accepted" | "rejected" | "resolved";

interface FeedbackReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feedback: {
    id: string;
    mcq_id: string;
    user_id: string;
    feedback_type: string;
    feedback_text: string;
    suggested_correction: string | null;
    status: FeedbackStatus;
    admin_response: string | null;
    created_at: string;
    is_paid_user: boolean;
  };
}

export function FeedbackReviewDialog({
  open,
  onOpenChange,
  feedback,
}: FeedbackReviewDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [adminResponse, setAdminResponse] = useState(feedback.admin_response || "");
  const [updating, setUpdating] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateSearchQuery, setDuplicateSearchQuery] = useState("");
  const [selectedDuplicateId, setSelectedDuplicateId] = useState<string | null>(null);

  const { data: mcq } = useQuery({
    queryKey: ["mcq-detail", feedback.mcq_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mcqs")
        .select("*")
        .eq("id", feedback.mcq_id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const { data: similarFeedback } = useQuery({
    queryKey: ["similar-feedback", feedback.mcq_id, duplicateSearchQuery],
    queryFn: async () => {
      let query = supabase
        .from("mcq_user_feedback")
        .select("id, feedback_text, created_at, status")
        .eq("mcq_id", feedback.mcq_id)
        .neq("id", feedback.id)
        .order("created_at", { ascending: false })
        .limit(10);

      if (duplicateSearchQuery) {
        query = query.ilike("feedback_text", `%${duplicateSearchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: showDuplicateDialog && !!feedback.mcq_id,
  });

  const updateFeedbackStatus = async (newStatus: FeedbackStatus, createTask = false) => {
    if (!user) return;

    setUpdating(true);

    try {
      const { error } = await supabase
        .from("mcq_user_feedback")
        .update({
          status: newStatus,
          admin_response: adminResponse || null,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", feedback.id);

      if (error) throw error;

      // Create quality improvement task if accepted
      if (createTask && newStatus === "accepted") {
        const { error: taskError } = await supabase
          .from("quality_improvement_tasks")
          .insert({
            task_description: `User feedback: ${feedback.feedback_text}`,
            priority: "medium",
            status: "open",
            assigned_to: user.id,
          });

        if (taskError) throw taskError;

        toast({
          title: "Feedback accepted",
          description: "Quality improvement task created successfully.",
        });
      } else {
        toast({
          title: "Feedback updated",
          description: `Status changed to ${newStatus}.`,
        });
      }

      // Award gamification points for accepted feedback
      if (newStatus === "accepted" || newStatus === "resolved") {
        const points = newStatus === "accepted" ? 50 : 25;
        try {
          await supabase.from("user_achievements").insert({
            user_id: feedback.user_id,
            achievement_type: "feedback",
            achievement_key: newStatus === "accepted" ? "feedback_accepted" : "feedback_resolved",
            title: newStatus === "accepted" ? "Feedback Accepted!" : "Feedback Resolved!",
            description: `Your feedback was ${newStatus}. Keep contributing!`,
            points,
            icon: "trophy",
          });
        } catch (err) {
          console.error("Achievement error:", err);
        }
      }

      queryClient.invalidateQueries({ queryKey: ["user-feedback"] });
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating feedback:", error);
      toast({
        title: "Update failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkDuplicate = async () => {
    if (!selectedDuplicateId || !user) {
      toast({
        title: "Selection required",
        description: "Please select an original feedback to link to",
        variant: "destructive",
      });
      return;
    }

    setUpdating(true);

    try {
      const { error } = await supabase
        .from("mcq_user_feedback")
        .update({
          status: "resolved",
          duplicate_of_id: selectedDuplicateId,
          admin_response: `This is a duplicate of another feedback submission. ${adminResponse}`,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", feedback.id);

      if (error) throw error;

      toast({
        title: "Marked as duplicate",
        description: "Feedback has been linked to the original submission.",
      });

      queryClient.invalidateQueries({ queryKey: ["user-feedback"] });
      setShowDuplicateDialog(false);
      onOpenChange(false);
    } catch (error) {
      console.error("Error marking as duplicate:", error);
      toast({
        title: "Update failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review User Feedback</DialogTitle>
            <DialogDescription>
              Review and respond to this MCQ improvement suggestion
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Feedback Details */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant={feedback.is_paid_user ? "default" : "secondary"}>
                  {feedback.is_paid_user ? "Paid User" : "Free User"}
                </Badge>
                <Badge variant="outline">{feedback.feedback_type.replace(/_/g, " ")}</Badge>
                <span className="text-sm text-muted-foreground">
                  Submitted {format(new Date(feedback.created_at), "MMM d, yyyy 'at' h:mm a")}
                </span>
              </div>

              <div className="p-4 bg-muted rounded-lg space-y-2">
                <Label>User's Feedback:</Label>
                <p className="text-sm">{feedback.feedback_text}</p>
              </div>

              {feedback.suggested_correction && (
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-2">
                  <Label>Suggested Correction:</Label>
                  <p className="text-sm">{feedback.suggested_correction}</p>
                </div>
              )}
            </div>

            <Separator />

            {/* MCQ Display */}
            {mcq && (
              <div className="space-y-4">
                <Label className="text-base">Current MCQ:</Label>
                <div className="p-4 border rounded-lg space-y-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Question Stem:</Label>
                    <p className="text-sm mt-1">{mcq.stem}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {["option_a", "option_b", "option_c", "option_d", "option_e"].map((opt) => {
                      if (!mcq[opt]) return null;
                      const isCorrect = mcq.correct_option === opt.split("_")[1].toUpperCase();
                      return (
                        <div
                          key={opt}
                          className={`p-3 rounded-md border ${
                            isCorrect ? "bg-green-50 border-green-200" : ""
                          }`}
                        >
                          <Label className="text-xs font-semibold">
                            {opt.split("_")[1].toUpperCase()}
                            {isCorrect && " ✓"}
                          </Label>
                          <p className="text-sm mt-1">{mcq[opt]}</p>
                        </div>
                      );
                    })}
                  </div>
                  {mcq.explanation && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Explanation:</Label>
                      <p className="text-sm mt-1">{mcq.explanation}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <Separator />

            {/* Admin Response */}
            <div className="space-y-2">
              <Label htmlFor="admin-response">Your Response (optional)</Label>
              <Textarea
                id="admin-response"
                placeholder="Add a response to the user explaining your decision..."
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={updating}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowDuplicateDialog(true)}
              disabled={updating}
            >
              <Copy className="h-4 w-4 mr-2" />
              Mark as Duplicate
            </Button>
            <Button
              variant="destructive"
              onClick={() => updateFeedbackStatus("rejected")}
              disabled={updating}
            >
              {updating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button
              variant="default"
              onClick={() => updateFeedbackStatus("accepted", true)}
              disabled={updating}
            >
              {updating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <ListTodo className="h-4 w-4 mr-2" />
              Accept & Create Task
            </Button>
            <Button
              variant="default"
              onClick={() => updateFeedbackStatus("resolved")}
              disabled={updating}
            >
              {updating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Mark Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Duplicate</AlertDialogTitle>
            <AlertDialogDescription>
              Search for and select the original feedback this is a duplicate of
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search similar feedback..."
                value={duplicateSearchQuery}
                onChange={(e) => setDuplicateSearchQuery(e.target.value)}
              />
              <Button size="icon" variant="outline">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            <div className="max-h-64 overflow-y-auto space-y-2">
              {similarFeedback?.map((similar) => (
                <div
                  key={similar.id}
                  className={`p-3 border rounded-lg cursor-pointer hover:bg-accent ${
                    selectedDuplicateId === similar.id ? "border-primary bg-accent" : ""
                  }`}
                  onClick={() => setSelectedDuplicateId(similar.id)}
                >
                  <div className="flex items-start justify-between">
                    <p className="text-sm flex-1">{similar.feedback_text}</p>
                    <Badge variant="outline" className="ml-2">{similar.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {format(new Date(similar.created_at), "MMM d, yyyy")}
                  </p>
                </div>
              ))}
              {similarFeedback?.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No similar feedback found
                </p>
              )}
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkDuplicate} disabled={!selectedDuplicateId || updating}>
              {updating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Mark as Duplicate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
